import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Mail, FileText, Globe, Send, GraduationCap } from "lucide-react";
import Layout from "../components/Layout";
import AiOutputDisclaimer from "../components/AiOutputDisclaimer";
import { api } from "../lib/api";
import { errMsg } from "../lib/errors";

export default function CasePresentation() {
  const { plan_id } = useParams();
  const [plan, setPlan] = useState(null);
  const [language, setLanguage] = useState("en");
  const [generating, setGenerating] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [presentation, setPresentation] = useState(null);
  const [education, setEducation] = useState(null);
  const [patientEmail, setPatientEmail] = useState("");
  const [patientFirstName, setPatientFirstName] = useState("");
  const [includeOptions, setIncludeOptions] = useState(["A", "B", "C"]);
  const [portalUrl, setPortalUrl] = useState("");

  useEffect(() => {
    api.get(`/treatment-plan/${plan_id}`).then((r) => {
      setPlan(r.data);
      const nm = (r.data?.patient_name || "").split(" ")[0];
      if (nm) setPatientFirstName(nm);
    }).catch((e) => toast.error(errMsg(e, "Failed to load plan")));
  }, [plan_id]);

  const generatePresentation = async () => {
    setGenerating(true);
    try {
      const r = await api.post("/case-presentation/generate", { treatment_plan_id: plan_id, language });
      setPresentation(r.data.presentation);
      toast.success("Case presentation ready");
    } catch (e) {
      toast.error(errMsg(e, "Generation failed"));
    } finally { setGenerating(false); }
  };

  const generateEducation = async () => {
    if (!plan?.patient_id) { toast.error("Plan needs a patient"); return; }
    setGenerating(true);
    try {
      const r = await api.post("/patient-education/generate", {
        patient_id: plan.patient_id, treatment_plan_id: plan_id, language, topics: [],
      });
      setEducation(r.data.content);
      toast.success("Patient education ready");
    } catch (e) {
      toast.error(errMsg(e, "Education generation failed"));
    } finally { setGenerating(false); }
  };

  const emailPlan = async () => {
    if (!patientEmail) { toast.error("Patient email required"); return; }
    setEmailing(true);
    try {
      const r = await api.post(`/treatment-plan/${plan_id}/email-patient`, {
        patient_email: patientEmail,
        patient_first_name: patientFirstName,
        include_options: includeOptions,
        language,
      });
      toast.success("Email sent and portal link created");
      setPortalUrl(r.data.portal_url);
    } catch (e) {
      toast.error(errMsg(e, "Email failed"));
    } finally { setEmailing(false); }
  };

  const toggleOpt = (o) => setIncludeOptions((arr) => arr.includes(o) ? arr.filter((x) => x !== o) : [...arr, o]);

  if (!plan) return <Layout><div className="text-sm text-[var(--pa-text-muted)]"><Loader2 size={14} className="animate-spin inline mr-2" /> Loading…</div></Layout>;

  return (
    <Layout>
      <div className="kicker">Phase 3 · Patient-facing</div>
      <h1 data-testid="presentation-title" className="font-serif text-3xl text-[var(--pa-text)] mt-1 flex items-center gap-3">
        <FileText size={22} className="text-[var(--pa-gold)]" /> Case Presentation — {plan.patient_name}
      </h1>

      {/* Language */}
      <div className="mt-5 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-1 border border-[var(--pa-border)] rounded p-0.5" data-testid="lang-switch">
          {[["en", "English"], ["es", "Español"]].map(([k, label]) => (
            <button key={k} onClick={() => setLanguage(k)} data-testid={`lang-${k}`}
              className={`px-3 py-1 text-xs rounded transition ${language === k ? "bg-[var(--pa-gold)] text-[var(--pa-bg)] font-semibold" : "text-[var(--pa-text-muted)]"}`}>
              <Globe size={11} className="inline mr-1" />{label}
            </button>
          ))}
        </div>
        <button onClick={generatePresentation} disabled={generating} data-testid="gen-presentation"
          className="flex items-center gap-2 bg-[var(--pa-gold)] text-[var(--pa-bg)] font-semibold px-4 py-2 rounded text-sm hover:bg-[var(--pa-gold-hover)] disabled:opacity-60">
          {generating ? <Loader2 size={12} className="animate-spin" /> : <FileText size={12} />} Generate 8-section presentation
        </button>
        <button onClick={generateEducation} disabled={generating} data-testid="gen-education"
          className="flex items-center gap-2 border border-[var(--pa-border)] text-[var(--pa-text)] px-4 py-2 rounded text-sm hover:border-[var(--pa-gold)] disabled:opacity-60">
          <GraduationCap size={12} /> Generate patient education
        </button>
      </div>

      {/* One-click email plan */}
      <section className="mt-7 border border-[var(--pa-gold)]/40 bg-[var(--pa-gold)]/5 rounded-md p-5" data-testid="email-card">
        <div className="kicker mb-2">One-click email this plan to patient</div>
        <div className="grid sm:grid-cols-2 gap-3">
          <input value={patientEmail} onChange={(e) => setPatientEmail(e.target.value)} type="email"
            placeholder="patient@example.com" data-testid="patient-email"
            className="bg-[var(--pa-bg-2)] border border-[var(--pa-border)] focus:border-[var(--pa-gold)] focus:outline-none rounded text-sm text-[var(--pa-text)] p-2.5" />
          <input value={patientFirstName} onChange={(e) => setPatientFirstName(e.target.value)}
            placeholder="Patient first name" data-testid="patient-first-name"
            className="bg-[var(--pa-bg-2)] border border-[var(--pa-border)] focus:border-[var(--pa-gold)] focus:outline-none rounded text-sm text-[var(--pa-text)] p-2.5" />
        </div>
        <div className="flex items-center gap-2 mt-3">
          <span className="text-[10px] uppercase tracking-wider text-[var(--pa-text-dim)]">Include options</span>
          {["A", "B", "C"].map((o) => (
            <button key={o} onClick={() => toggleOpt(o)} data-testid={`opt-${o}`}
              className={`text-xs px-2.5 py-1 rounded border ${includeOptions.includes(o) ? "bg-[var(--pa-gold)] text-[var(--pa-bg)] border-[var(--pa-gold)] font-semibold" : "bg-[var(--pa-bg-2)] text-[var(--pa-text-muted)] border-[var(--pa-border)]"}`}>
              {o}
            </button>
          ))}
        </div>
        <button onClick={emailPlan} disabled={emailing} data-testid="email-plan-submit"
          className="mt-4 flex items-center gap-2 bg-[var(--pa-gold)] text-[var(--pa-bg)] font-semibold px-5 py-2.5 rounded hover:bg-[var(--pa-gold-hover)] disabled:opacity-60">
          {emailing ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Send plan + portal link
        </button>
        {portalUrl && (
          <div className="mt-3 text-xs text-[var(--pa-text-muted)]" data-testid="portal-url">
            Portal link: <a href={portalUrl} target="_blank" rel="noopener noreferrer" className="text-[var(--pa-gold)] underline break-all">{portalUrl}</a>
          </div>
        )}
      </section>

      {/* Presentation render */}
      {presentation && (
        <section className="mt-8" data-testid="presentation-render">
          <div className="kicker mb-3">Generated presentation ({presentation.language})</div>
          <AiOutputDisclaimer className="mb-4" />
          <div className="space-y-4">
            {(presentation.sections || []).map((s, i) => (
              <div key={i} className="border border-[var(--pa-border)] bg-[var(--pa-bg-2)] rounded p-4">
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="w-6 h-6 rounded-full bg-[var(--pa-gold)] text-[var(--pa-bg)] text-xs font-bold flex items-center justify-center">{s.n}</span>
                  <span className="font-serif text-lg text-[var(--pa-text)]">{s.title}</span>
                </div>
                <pre className="text-sm text-[var(--pa-text-muted)] whitespace-pre-wrap font-sans leading-relaxed">{s.body_markdown}</pre>
              </div>
            ))}
            {presentation.consent_summary && (
              <div className="border border-[var(--pa-gold)]/30 bg-[var(--pa-gold)]/5 rounded p-4">
                <div className="kicker text-[var(--pa-gold)] mb-1">Consent summary</div>
                <p className="text-sm text-[var(--pa-text-muted)]">{presentation.consent_summary}</p>
              </div>
            )}
          </div>
        </section>
      )}

      {education && (
        <section className="mt-8" data-testid="education-render">
          <div className="kicker mb-3">Patient education ({education.language})</div>
          <AiOutputDisclaimer className="mb-4" />
          <div className="space-y-4">
            {(education.topics || []).map((t, i) => (
              <div key={i} className="border border-[var(--pa-border)] bg-[var(--pa-bg-2)] rounded p-4">
                <div className="font-serif text-lg text-[var(--pa-text)]">{t.title_friendly}</div>
                <div className="text-[11px] text-[var(--pa-text-dim)] font-mono mt-0.5">{t.topic_slug}</div>
                <pre className="mt-2 text-sm text-[var(--pa-text-muted)] whitespace-pre-wrap font-sans leading-relaxed">{t.summary_markdown}</pre>
                {t.pre_op_checklist?.length > 0 && (
                  <div className="mt-2"><div className="kicker mb-1">Pre-op checklist</div>
                    <ul className="text-xs text-[var(--pa-text-muted)] list-disc ml-5">{t.pre_op_checklist.map((x, k) => <li key={k}>{x}</li>)}</ul>
                  </div>
                )}
                {t.post_op_instructions_markdown && (
                  <div className="mt-2"><div className="kicker mb-1">Post-op</div>
                    <pre className="text-xs text-[var(--pa-text-muted)] whitespace-pre-wrap font-sans">{t.post_op_instructions_markdown}</pre>
                  </div>
                )}
                {t.warning_signs?.length > 0 && (
                  <div className="mt-2"><div className="kicker text-red-300 mb-1">Warning signs</div>
                    <ul className="text-xs text-red-300 list-disc ml-5">{t.warning_signs.map((x, k) => <li key={k}>{x}</li>)}</ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}
    </Layout>
  );
}
